import { graphFetch, graphJson, GraphError } from "../graph/client.ts";
import { collect } from "../graph/paging.ts";
import type { ServiceName } from "../graph/throttle.ts";
import { summarizeMailSize, type MailFolderSizeInfo, type SizeEstimate } from "./mail-size.ts";
import type { ModuleId } from "../auth/config.ts";

/**
 * Preflight (ARCHITECTURE.md §10 items 3–4): one live read per module to
 * surface a Conditional Access block before the export itself hits it, plus a
 * best-effort size/count estimate. Deliberately informational, not a hard
 * gate — a module that fails its check here will surface the identical
 * failure in its own export panel regardless, so there is nothing this layer
 * could usefully block that the export flow doesn't already handle.
 */

export type { SizeEstimate };

/**
 * A cheap read per module — enough to prove the scope works against this
 * specific tenant's Conditional Access policy, not just that a token was
 * issued. `service` matters, not just `path`: OneDrive content is throttled
 * separately from Outlook endpoints (§9), and a preflight check that always
 * charged against the "outlook" budget would be silently wrong for it.
 * Modules with no entry here have no check defined yet.
 */
const PREFLIGHT_CHECK: Partial<Record<ModuleId, { path: string; service: ServiceName }>> = {
    teams: { path: "/me/chats?$top=1", service: "outlook" },
    mail: { path: "/me/mailFolders?$top=1", service: "outlook" },
    onedrive: { path: "/me/drive?$select=id", service: "drive" },
    tasks: { path: "/me/todo/lists?$top=1", service: "outlook" },
    contacts: { path: "/me/contacts?$top=1", service: "outlook" },
    calendar: { path: "/me/calendars?$top=1", service: "outlook" },
    onenote: { path: "/me/onenote/notebooks?$top=1", service: "outlook" }
};

export interface ModuleCheckResult {
    moduleId: ModuleId;
    ok: boolean;
    /** Status + code summary, only present when ok is false. */
    error?: string;
}

/**
 * Confirms one module's scope actually works against this tenant right now.
 * Does not catch InteractionRequiredError/NotSignedInError — those are a
 * broader auth condition the caller already knows how to handle (the consent
 * flow), not a per-module reachability problem to report here.
 */
export async function checkModuleReachable(
    moduleId: ModuleId,
    scopes: readonly string[]
): Promise<ModuleCheckResult> {
    const check = PREFLIGHT_CHECK[moduleId];
    if (!check) {
        return { moduleId, ok: true };
    }

    try {
        await graphFetch(check.path, { scopes, service: check.service });
        return { moduleId, ok: true };
    } catch (err) {
        if (err instanceof GraphError) {
            return { moduleId, ok: false, error: `${err.status} ${err.code}` };
        }
        throw err;
    }
}

export async function estimateMailSize(scopes: readonly string[]): Promise<SizeEstimate> {
    const folders = await collect<MailFolderSizeInfo>(
        "/me/mailFolders?$select=totalItemCount&$top=100",
        { scopes, service: "outlook" }
    );
    return summarizeMailSize(folders);
}

/**
 * Teams has no size API at all (§10) — chat count is the only number
 * available without enumerating every chat's messages, which §10 itself
 * only suggests as a rough extrapolation, not something to build for a
 * count this cheap to just... count.
 */
export async function estimateTeamsCount(scopes: readonly string[]): Promise<SizeEstimate> {
    const chats = await collect<{ id: string }>("/me/chats?$top=50", {
        scopes,
        service: "outlook"
    });
    return { items: chats.length };
}

/**
 * List *count*, not task count (§10) — the same reasoning as Teams' chat
 * count: the actual number of tasks is only knowable by listing every list's
 * tasks, which is the export itself, not a cheap preflight read. A To Do
 * user typically has a handful of lists, so this is a far weaker signal than
 * Teams' chat count, but it is the only one available without paying for
 * the full walk twice.
 */
export async function estimateTasksCount(scopes: readonly string[]): Promise<SizeEstimate> {
    const lists = await collect<{ id: string }>("/me/todo/lists?$top=50", {
        scopes,
        service: "outlook"
    });
    return { items: lists.length };
}

/**
 * Contact count via a plain, id-only listing (§10) — the same shape as
 * Teams' chat count. Contacts has no $count support worth depending on here,
 * and unlike Mail/OneDrive there is no separate per-item content fetch this
 * would be duplicating the cost of: the listing itself already carries every
 * field the export needs, so this is cheap relative to what it estimates.
 */
export async function estimateContactsCount(scopes: readonly string[]): Promise<SizeEstimate> {
    const contacts = await collect<{ id: string }>("/me/contacts?$select=id&$top=100", {
        scopes,
        service: "outlook"
    });
    return { items: contacts.length };
}

/**
 * Calendar *count*, not event count (§10) — the same reasoning as Tasks'
 * list count: the number of events is only knowable by delta-walking every
 * calendar, which is the export itself.
 */
export async function estimateCalendarCount(scopes: readonly string[]): Promise<SizeEstimate> {
    const calendars = await collect<{ id: string }>("/me/calendars?$select=id&$top=50", {
        scopes,
        service: "outlook"
    });
    return { items: calendars.length };
}

/**
 * Notebook *count*, not page count (§10) — the same reasoning as Tasks' list
 * count and Calendar's calendar count: the number of pages is only knowable
 * by walking every notebook's sections and pages, which is the export
 * itself.
 */
export async function estimateOneNoteCount(scopes: readonly string[]): Promise<SizeEstimate> {
    const notebooks = await collect<{ id: string }>("/me/onenote/notebooks?$select=id&$top=50", {
        scopes,
        service: "outlook"
    });
    return { items: notebooks.length };
}

interface DriveQuota {
    quota?: { used?: number };
}

/**
 * The one exact size estimate available anywhere in preflight (§10) —
 * `quota.used` is the real number, not a heuristic. No item count comes with
 * it; OneDrive's quota endpoint simply doesn't report one, and estimating a
 * file count would need a full tree walk this check is specifically meant to
 * avoid paying for twice.
 */
export async function estimateOneDriveSize(scopes: readonly string[]): Promise<SizeEstimate> {
    const drive = await graphJson<DriveQuota>("/me/drive?$select=quota", {
        scopes,
        service: "drive"
    });
    return drive.quota?.used !== undefined ? { bytes: drive.quota.used } : {};
}
